import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { checklistAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import ContentHeader from '../components/ContentHeader';
import InfoIconButton from '../components/InfoIconButton';
import './ChecklistTemplates.css';

const CHECKLIST_SAVE_CHANGES_HINT =
  'Save changes stores the checklist name, notes, and every edited item row (description, required, work/task types, more info). Reorder arrows save order immediately. Uncheck Active on a line to retire it from new checklists; existing referral checklists keep that row and still show the text.';

function ChecklistTemplates() {
  const { typeId } = useParams();
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [types, setTypes] = useState([]);
  const [typeDetail, setTypeDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [typeForm, setTypeForm] = useState({ type_name: '', notes: '' });
  const [itemEdits, setItemEdits] = useState({});
  const [newItemForm, setNewItemForm] = useState({
    item_description: '',
    more_info: '',
    required: false,
    sort_order: '',
    work_type_id: '',
    task_type_id: '',
  });
  const [showAddTypeModal, setShowAddTypeModal] = useState(false);
  const [addTypeForm, setAddTypeForm] = useState({ type_name: '', notes: '' });
  const [includeInactiveTypes, setIncludeInactiveTypes] = useState(false);
  const [hideInactiveTemplateItems, setHideInactiveTemplateItems] = useState(false);
  const [showActiveHint, setShowActiveHint] = useState(false);
  const [showItemActiveHint, setShowItemActiveHint] = useState(false);
  const [saveHintOpenTop, setSaveHintOpenTop] = useState(false);
  const [saveHintOpenBottom, setSaveHintOpenBottom] = useState(false);
  const [workTaskOptions, setWorkTaskOptions] = useState({ workTypes: [], taskTypes: [] });

  useEffect(() => {
    if (!hasPermission('checklists.manage_templates')) {
      navigate(tenantPath('/settings'), { replace: true });
      return;
    }
    loadTypes();
  }, [navigate, tenantPath]);

  useEffect(() => {
    setSaveHintOpenTop(false);
    setSaveHintOpenBottom(false);
    if (typeId) {
      loadTypeDetail(typeId);
    } else {
      setTypeDetail(null);
    }
  }, [typeId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await checklistAPI.getAdminWorkTaskOptions();
        setWorkTaskOptions(res.data?.data || { workTypes: [], taskTypes: [] });
      } catch {
        setWorkTaskOptions({ workTypes: [], taskTypes: [] });
      }
    })();
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    if (!typeDetail || !typeId) return false;
    if ((typeForm.type_name.trim() || '') !== (typeDetail.type_name || '').trim()) return true;
    if ((typeForm.notes || '') !== (typeDetail.notes || '')) return true;
    for (const id of Object.keys(itemEdits)) {
      if (Object.keys(itemEdits[id] || {}).length > 0) return true;
    }
    if (newItemForm.item_description.trim()) return true;
    if (newItemForm.more_info.trim()) return true;
    if (newItemForm.required) return true;
    if (newItemForm.work_type_id || newItemForm.task_type_id) return true;
    return false;
  }, [typeDetail, typeId, typeForm.type_name, typeForm.notes, itemEdits, newItemForm]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await checklistAPI.getTypes();
      setTypes(res.data?.data ?? []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load checklist types');
      setTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTypeDetail = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const res = await checklistAPI.getTypeWithItems(id);
      const data = res.data?.data ?? null;
      setTypeDetail(data);
      setTypeForm({
        type_name: data?.type_name ?? '',
        notes: data?.notes ?? '',
      });
      setItemEdits({});
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load checklist type');
      setTypeDetail(null);
    } finally {
      setLoading(false);
    }
  };

  /** Build PATCH body for an item row from local edits (used by Save all). */
  const buildItemPatchBody = (edit) => {
    const body = {};
    if ('item_description' in edit) body.item_description = edit.item_description;
    if ('more_info' in edit) body.more_info = edit.more_info;
    if ('required' in edit) body.required = edit.required;
    if ('sort_order' in edit) body.sort_order = edit.sort_order;
    if ('work_type_id' in edit) body.work_type_id = edit.work_type_id || null;
    if ('task_type_id' in edit) body.task_type_id = edit.task_type_id || null;
    return body;
  };

  const validateItemWorkTaskPair = (item, edit) => {
    const effectiveWorkTypeId = ('work_type_id' in edit) ? (edit.work_type_id || '') : (item.work_type_id || '');
    const effectiveTaskTypeId = ('task_type_id' in edit) ? (edit.task_type_id || '') : (item.task_type_id || '');
    if (!!effectiveWorkTypeId !== !!effectiveTaskTypeId) {
      return 'Select both Work type and Task type, or leave both empty.';
    }
    return null;
  };

  const handleSaveAll = async () => {
    if (!typeId || !typeDetail) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await checklistAPI.updateType(typeId, {
        type_name: typeForm.type_name.trim() || typeDetail.type_name,
        notes: typeForm.notes === '' ? null : typeForm.notes,
      });
      setTypeDetail((prev) => prev && { ...prev, type_name: typeForm.type_name.trim() || prev.type_name, notes: typeForm.notes });

      const sortedItems = (typeDetail.items || [])
        .slice()
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      const itemsToPersist = sortedItems.filter(
        (item) => itemEdits[item.id] && Object.keys(itemEdits[item.id]).length > 0
      );

      for (const item of itemsToPersist) {
        const edit = itemEdits[item.id];
        const pairErr = validateItemWorkTaskPair(item, edit);
        if (pairErr) {
          setSaveMessage(pairErr);
          return;
        }
        const body = buildItemPatchBody(edit);
        if (Object.keys(body).length === 0) continue;
        await checklistAPI.updateItemTemplate(item.id, body);
      }

      if (itemsToPersist.length > 0) {
        setItemEdits((prev) => {
          const next = { ...prev };
          for (const item of itemsToPersist) delete next[item.id];
          return next;
        });
        await loadTypeDetail(typeId);
      }

      setSaveMessage('Saved.');
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDetail = () => {
    if (saving) return;
    if (hasUnsavedChanges) {
      if (!window.confirm('Discard unsaved changes?')) return;
    }
    navigate(tenantPath('/settings/checklist-templates'));
  };

  const setItemEdit = (itemId, field, value) => {
    setItemEdits((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [field]: value },
    }));
  };

  const getItemValue = (item, field) => {
    const edit = itemEdits[item.id];
    if (edit && field in edit) return edit[field];
    return item[field];
  };

  const handleMoveUp = async (item) => {
    const sorted = (typeDetail.items || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const idx = sorted.findIndex((i) => i.id === item.id);
    if (idx <= 0) return;
    const prev = sorted[idx - 1];
    const thisOrder = item.sort_order ?? idx;
    const prevOrder = prev.sort_order ?? idx - 1;
    setSaving(true);
    setSaveMessage(null);
    try {
      await Promise.all([
        checklistAPI.updateItemTemplate(item.id, { sort_order: prevOrder }),
        checklistAPI.updateItemTemplate(prev.id, { sort_order: thisOrder }),
      ]);
      await loadTypeDetail(typeId);
      setSaveMessage('Order updated.');
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to reorder');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveDown = async (item) => {
    const sorted = (typeDetail.items || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const idx = sorted.findIndex((i) => i.id === item.id);
    if (idx < 0 || idx >= sorted.length - 1) return;
    const next = sorted[idx + 1];
    const thisOrder = item.sort_order ?? idx;
    const nextOrder = next.sort_order ?? idx + 1;
    setSaving(true);
    setSaveMessage(null);
    try {
      await Promise.all([
        checklistAPI.updateItemTemplate(item.id, { sort_order: nextOrder }),
        checklistAPI.updateItemTemplate(next.id, { sort_order: thisOrder }),
      ]);
      await loadTypeDetail(typeId);
      setSaveMessage('Order updated.');
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to reorder');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateType = async () => {
    const name = addTypeForm.type_name.trim();
    if (!name) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const res = await checklistAPI.createType({
        type_name: name,
        notes: addTypeForm.notes.trim() || null,
      });
      const newType = res.data?.data;
      setShowAddTypeModal(false);
      setAddTypeForm({ type_name: '', notes: '' });
      if (newType?.id) {
        navigate(tenantPath(`/settings/checklist-templates/${newType.id}`));
      } else {
        await loadTypes();
        setSaveMessage('Checklist type created.');
      }
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to create type');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async () => {
    const desc = newItemForm.item_description.trim();
    if (!desc || !typeId) return;
    if (!!newItemForm.work_type_id !== !!newItemForm.task_type_id) {
      setSaveMessage('Select both Work type and Task type, or leave both empty.');
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    try {
      const body = {
        item_description: desc,
        more_info: newItemForm.more_info.trim() || null,
        required: !!newItemForm.required,
        work_type_id: newItemForm.work_type_id || null,
        task_type_id: newItemForm.task_type_id || null,
      };
      if (newItemForm.sort_order !== '' && Number.isInteger(Number(newItemForm.sort_order))) {
        body.sort_order = parseInt(newItemForm.sort_order, 10);
      }
      await checklistAPI.createItemTemplate(typeId, body);
      setNewItemForm({
        item_description: '',
        more_info: '',
        required: false,
        sort_order: '',
        work_type_id: '',
        task_type_id: '',
      });
      await loadTypeDetail(typeId);
      setSaveMessage('Item added.');
    } catch (err) {
      setSaveMessage(err.response?.data?.error || err.message || 'Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  if (!hasPermission('checklists.manage_templates')) {
    return null;
  }

  if (loading && !typeDetail && !types.length) {
    return (
      <div className="checklist-templates-page">
        <ContentHeader title="Checklist templates" />
        <p className="loading-msg">Loading...</p>
      </div>
    );
  }

  if (error && !typeDetail) {
    return (
      <div className="checklist-templates-page">
        <ContentHeader title="Checklist templates" />
        <p className="error-msg">{error}</p>
      </div>
    );
  }

  if (!typeId) {
    return (
      <div className="checklist-templates-page">
        <ContentHeader
          title="Checklist templates"
          titleHint="Edit checklist types and their item templates (descriptions, order, required flag, more info). Changes apply when new checklists are added for a referral."
          actions={
            <button
              type="button"
              onClick={() => { setShowAddTypeModal(true); setSaveMessage(null); }}
              className="btn-add-type"
            >
              + Add checklist type
            </button>
          }
        />
        {saveMessage && <p className={saveMessage.startsWith('Failed') ? 'error-msg' : 'save-msg'}>{saveMessage}</p>}
        {types.length === 0 && !showAddTypeModal ? (
          <p className="no-types">No checklist types yet. Add one above or run the checklist seed script for your tenant.</p>
        ) : (
          <>
            <div className="checklist-types-filter">
              <label className="checklist-types-active-filter">
                Active
                <input
                  type="checkbox"
                  checked={!includeInactiveTypes}
                  onChange={(e) => setIncludeInactiveTypes(!e.target.checked)}
                  aria-label="Show active types only"
                  title="Uncheck to show inactive types"
                />
              </label>
            </div>
            <ul className="checklist-types-list">
              {(includeInactiveTypes ? types : types.filter((t) => t.is_active !== false)).map((t) => (
                <li key={t.id}>
                  <Link to={tenantPath(`/settings/checklist-templates/${t.id}`)} className="checklist-type-link">
                    {t.type_name}
                  </Link>
                  <span className="checklist-type-status">{t.is_active !== false && t.is_active !== undefined ? 'Active' : 'Inactive'}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        {showAddTypeModal && (
          <div className="checklist-modal-overlay" onClick={() => !saving && setShowAddTypeModal(false)}>
            <div className="checklist-modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Add checklist type</h3>
              <p className="modal-hint">Create a new checklist type (e.g. Intake, 6-Month Review). You can add items to it after creating.</p>
              <div className="form-row">
                <label>Name</label>
                <input
                  type="text"
                  value={addTypeForm.type_name}
                  onChange={(e) => setAddTypeForm((f) => ({ ...f, type_name: e.target.value }))}
                  className="input-text"
                  placeholder="e.g. Annual Review"
                />
              </div>
              <div className="form-row">
                <label>Notes (optional)</label>
                <textarea
                  value={addTypeForm.notes}
                  onChange={(e) => setAddTypeForm((f) => ({ ...f, notes: e.target.value }))}
                  className="input-textarea"
                  rows={2}
                  placeholder="Shown at top of this checklist"
                />
              </div>
              {saveMessage && <p className="error-msg">{saveMessage}</p>}
              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddTypeModal(false)} className="btn-cancel" disabled={saving}>Cancel</button>
                <button type="button" onClick={handleCreateType} className="btn-primary" disabled={saving || !addTypeForm.type_name.trim()}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!typeDetail) {
    return (
      <div className="checklist-templates-page">
        <ContentHeader title="Checklist templates" />
        <p className="loading-msg">Loading...</p>
      </div>
    );
  }

  const items = (typeDetail.items || [])
    .filter((it) => !hideInactiveTemplateItems || it.is_active !== false)
    .slice()
    .sort((a, b) => {
      const rank = (row) => (row.is_active === false ? 0 : 1);
      const r = rank(b) - rank(a);
      if (r !== 0) return r;
      return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });

  const renderDetailActionBar = (placement) => {
    const isTop = placement === 'top';
    const hintOpen = isTop ? saveHintOpenTop : saveHintOpenBottom;
    const setHintOpen = isTop ? setSaveHintOpenTop : setSaveHintOpenBottom;
    const hintId = `checklist-templates-save-hint-${placement}`;
    return (
      <div className="checklist-templates-detail-actions">
        <div className="checklist-templates-detail-actions-row">
          <div className="checklist-templates-detail-actions-leading">
            {hasUnsavedChanges ? (
              <span className="checklist-templates-unsaved" role="status">Unsaved changes</span>
            ) : null}
          </div>
          <div className="checklist-templates-detail-actions-buttons">
            <button
              type="button"
              onClick={handleSaveAll}
              disabled={saving || !hasUnsavedChanges}
              className="btn-save"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <InfoIconButton
              className="checklist-templates-save-hint-icon"
              onClick={() => setHintOpen((o) => !o)}
              ariaExpanded={hintOpen}
              ariaControls={hintId}
              ariaLabel="What Save changes does"
              ariaLabelExpanded="Hide Save changes help"
            />
          </div>
        </div>
        {hintOpen ? (
          <p id={hintId} className="checklist-templates-save-hint" role="region">
            {CHECKLIST_SAVE_CHANGES_HINT}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <div className="checklist-templates-page">
      <ContentHeader
        title={typeDetail.type_name}
        onBack={handleCancelDetail}
        backLabel="Back to template list"
        titleHint="Edits to items stay local until you click Save changes (top or bottom). Closing the tab or refreshing the browser warns if you have unsaved edits. Use Back to template list (under the title) to leave this screen safely (you will be prompted if there are unsaved changes). Click the info icon next to Save changes for details on what gets saved."
      />

      {saveMessage && <p className={saveMessage.startsWith('Failed') ? 'error-msg' : 'save-msg'}>{saveMessage}</p>}

      {renderDetailActionBar('top')}

      <section className="checklist-type-form">
        <div className="checklist-type-form-inner">
          <h2 className="checklist-type-form-heading">Checklist type</h2>
          <div className="form-row">
            <label>Name</label>
            <input
              type="text"
              value={typeForm.type_name}
              onChange={(e) => setTypeForm((f) => ({ ...f, type_name: e.target.value }))}
              className="input-text"
            />
          </div>
          <div className="form-row">
            <label>Notes (shown at top of checklist)</label>
            <textarea
              value={typeForm.notes}
              onChange={(e) => setTypeForm((f) => ({ ...f, notes: e.target.value }))}
              className="input-textarea"
              rows={3}
            />
          </div>
          <div className="form-row form-row-active">
            <div className="checklist-active-row">
              <label className="checklist-active-checkbox-label">
                <input
                  type="checkbox"
                  checked={typeDetail.is_active !== false && typeDetail.is_active !== undefined}
                  onChange={async () => {
                    const newActive = !(typeDetail.is_active !== false && typeDetail.is_active !== undefined);
                    setSaving(true);
                    setSaveMessage(null);
                    try {
                      await checklistAPI.updateType(typeId, { is_active: newActive });
                      setTypeDetail((prev) => prev && { ...prev, is_active: newActive });
                      setSaveMessage(newActive ? 'Type is now active.' : 'Type is now inactive.');
                    } catch (err) {
                      setSaveMessage(err.response?.data?.error || err.message || 'Failed to update');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                />
                <span>Active</span>
              </label>
              <InfoIconButton
                onClick={() => setShowActiveHint((v) => !v)}
                ariaExpanded={showActiveHint}
                ariaControls="checklist-active-hint"
                ariaLabel="Show explanation"
                ariaLabelExpanded="Hide explanation"
              />
            </div>
            {showActiveHint && (
              <p id="checklist-active-hint" className="checklist-active-hint-text">
                When unchecked, this type does not appear when adding a new checklist to a referral. Existing checklists of this type stay visible.
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="checklist-items-section">
        <div className="checklist-items-section-header">
          <h2>Items ({(typeDetail.items || []).length})</h2>
          <label className="checklist-items-active-filter">
            <input
              type="checkbox"
              checked={hideInactiveTemplateItems}
              onChange={(e) => setHideInactiveTemplateItems(e.target.checked)}
              aria-label="Hide inactive template lines"
            />
            <span>Hide inactive lines</span>
          </label>
        </div>
        {(items.length === 0 && !newItemForm.item_description.trim()) ? (
          <p className="no-items">No items in this type. Add one below or run the checklist seed script.</p>
        ) : null}
        <div className="items-table-wrap">
          <table className="checklist-items-table">
            <thead>
              <tr>
                <th className="col-order" title="Move up or down">Order</th>
                <th className="col-item-desc">Item description</th>
                <th className="col-required" title="Required">Req.</th>
                <th className="col-work-type">Work type</th>
                <th className="col-task-type">Task type</th>
                <th className="col-more">More info</th>
                <th className="col-item-active">
                  <span className="checklist-item-active-th">Active</span>
                  <InfoIconButton
                    onClick={() => setShowItemActiveHint((v) => !v)}
                    ariaExpanded={showItemActiveHint}
                    ariaControls="checklist-item-active-hint"
                    ariaLabel="Explain Active for template lines"
                    ariaLabelExpanded="Hide explanation"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {showItemActiveHint && (
                <tr className="checklist-item-active-hint-row">
                  <td colSpan={7} id="checklist-item-active-hint" className="checklist-item-active-hint-cell">
                    When unchecked, this line is not added to new referral checklists. Lines already on a referral checklist stay visible there.
                  </td>
                </tr>
              )}
              {items.map((item, index) => {
                const rowWorkTypeId = getItemValue(item, 'work_type_id') || '';
                /** Orphan task UUID when work is empty confuses the disabled select (wrong label); hide until work is set. */
                const rowTaskTypeId = rowWorkTypeId ? (getItemValue(item, 'task_type_id') || '') : '';
                return (
                  <tr key={item.id} className={item.is_active === false ? 'item-template-inactive' : ''}>
                    <td className="col-order">
                      <div className="order-buttons">
                        <button
                          type="button"
                          onClick={() => handleMoveUp(item)}
                          disabled={saving || index === 0}
                          className="btn-order"
                          title="Move up"
                          aria-label="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveDown(item)}
                          disabled={saving || index === items.length - 1}
                          className="btn-order"
                          title="Move down"
                          aria-label="Move down"
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                    <td className="col-item-desc">
                      <textarea
                        value={getItemValue(item, 'item_description') ?? ''}
                        onChange={(e) => setItemEdit(item.id, 'item_description', e.target.value)}
                        className="input-textarea item-desc full-width"
                        rows={3}
                      />
                    </td>
                    <td className="col-required">
                      <input
                        type="checkbox"
                        checked={!!getItemValue(item, 'required')}
                        onChange={(e) => setItemEdit(item.id, 'required', e.target.checked)}
                      />
                    </td>
                    <td className="col-work-type">
                      <select
                        value={getItemValue(item, 'work_type_id') || ''}
                        onChange={(e) => {
                          const nextWorkTypeId = e.target.value;
                          setItemEdit(item.id, 'work_type_id', nextWorkTypeId);
                          if (!nextWorkTypeId) setItemEdit(item.id, 'task_type_id', '');
                        }}
                        className="input-text"
                      >
                        <option value="">(none)</option>
                        {(workTaskOptions.workTypes || []).map((w) => (
                          <option key={w.id} value={w.id}>
                            {w.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="col-task-type">
                      <select
                        value={rowTaskTypeId}
                        onChange={(e) => setItemEdit(item.id, 'task_type_id', e.target.value)}
                        className="input-text"
                        disabled={!rowWorkTypeId}
                      >
                        <option value="">(none)</option>
                        {(workTaskOptions.taskTypes || []).map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="col-more">
                      <textarea
                        value={getItemValue(item, 'more_info') ?? ''}
                        onChange={(e) => setItemEdit(item.id, 'more_info', e.target.value)}
                        className="input-textarea small"
                        rows={2}
                        placeholder="Optional instructions"
                      />
                    </td>
                    <td className="col-item-active">
                      <input
                        type="checkbox"
                        checked={item.is_active !== false && item.is_active !== undefined}
                        onChange={async () => {
                          const newActive = !(item.is_active !== false && item.is_active !== undefined);
                          setSaving(true);
                          setSaveMessage(null);
                          try {
                            await checklistAPI.updateItemTemplate(item.id, { is_active: newActive });
                            setTypeDetail((prev) => {
                              if (!prev?.items) return prev;
                              return {
                                ...prev,
                                items: prev.items.map((row) =>
                                  row.id === item.id ? { ...row, is_active: newActive } : row
                                ),
                              };
                            });
                            setSaveMessage(newActive ? 'Line is active for new checklists.' : 'Line retired from new checklists.');
                          } catch (err) {
                            setSaveMessage(err.response?.data?.error || err.message || 'Failed to update');
                          } finally {
                            setSaving(false);
                          }
                        }}
                        disabled={saving}
                        title="Include on new referral checklists"
                        aria-label={`Active for new checklists: ${(item.item_description || '').slice(0, 80)}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="checklist-add-item-panel">
          <h3 className="checklist-add-item-heading">Add item</h3>
          <p className="checklist-add-item-intro">New items are saved to the server as soon as you click Add item (not part of Save changes).</p>
          <div className="checklist-add-item-grid">
            <div className="checklist-add-item-field checklist-add-item-field--wide">
              <label>Item description</label>
              <textarea
                value={newItemForm.item_description}
                onChange={(e) => setNewItemForm((f) => ({ ...f, item_description: e.target.value }))}
                className="input-textarea item-desc full-width"
                rows={3}
                placeholder="Required"
              />
            </div>
            <div className="checklist-add-item-field">
              <label>Required</label>
              <input
                type="checkbox"
                checked={!!newItemForm.required}
                onChange={(e) => setNewItemForm((f) => ({ ...f, required: e.target.checked }))}
              />
            </div>
            <div className="checklist-add-item-field">
              <label>Work type</label>
              <select
                value={newItemForm.work_type_id}
                onChange={(e) => setNewItemForm((f) => ({
                  ...f,
                  work_type_id: e.target.value,
                  task_type_id: e.target.value ? f.task_type_id : '',
                }))}
                className="input-text"
              >
                <option value="">(none)</option>
                {(workTaskOptions.workTypes || []).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="checklist-add-item-field">
              <label>Task type</label>
              <select
                value={newItemForm.task_type_id}
                onChange={(e) => setNewItemForm((f) => ({ ...f, task_type_id: e.target.value }))}
                className="input-text"
                disabled={!newItemForm.work_type_id}
              >
                <option value="">(none)</option>
                {(workTaskOptions.taskTypes || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="checklist-add-item-field checklist-add-item-field--wide">
              <label>More info</label>
              <textarea
                value={newItemForm.more_info}
                onChange={(e) => setNewItemForm((f) => ({ ...f, more_info: e.target.value }))}
                className="input-textarea small"
                rows={2}
                placeholder="Optional"
              />
            </div>
            <div className="checklist-add-item-actions">
              <button
                type="button"
                onClick={handleAddItem}
                disabled={saving || !newItemForm.item_description.trim()}
                className="btn-save-row btn-add-item"
              >
                Add item
              </button>
            </div>
          </div>
        </div>
      </section>

      {renderDetailActionBar('bottom')}
    </div>
  );
}

export default ChecklistTemplates;
